import { useEffect, useState } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

export function RecordPreviewDialog({ docId, open, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && docId) {
      setLoading(true);
      setDoc(null);
      api.get(`/documents/${docId}`)
        .then(({ data }) => setDoc(data))
        .catch(() => setDoc(null))
        .finally(() => setLoading(false));
    }
  }, [open, docId]);

  const type = (doc?.file_type || "").toLowerCase();
  const url = doc?.data_url || "";
  const isImage = type.startsWith("image/") || url.startsWith("data:image/");
  const isPdf = type.includes("pdf") || url.startsWith("data:application/pdf");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="record-preview-dialog"
        className="care-scope max-w-[400px] max-h-[88vh] overflow-y-auto no-scrollbar rounded-2xl bg-[#1A1A1A] border-[#333333] text-[#F8FAFC]"
      >
        <div className="flex items-center gap-2 pr-6">
          <FileText size={18} className="text-[#EF9F27] shrink-0" />
          <DialogTitle className="font-head font-bold truncate text-[#F8FAFC]">
            {doc?.label || "Record preview"}
          </DialogTitle>
        </div>

        <div className="mt-3">
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="animate-spin text-[#EF9F27]" />
            </div>
          ) : !doc ? (
            <p className="text-sm text-[#A1A1AA] py-8 text-center">Could not load this record.</p>
          ) : isImage ? (
            <img
              src={url}
              alt={doc.label}
              data-testid="record-preview-image"
              className="w-full rounded-xl border border-[#333333]"
            />
          ) : isPdf ? (
            <iframe
              src={url}
              title={doc.label}
              data-testid="record-preview-pdf"
              className="w-full h-[60vh] rounded-xl border border-[#333333] bg-white"
            />
          ) : (
            <div className="bg-[#111111] border border-[#333333] rounded-xl p-6 text-center">
              <FileText className="mx-auto text-[#666666]" size={32} />
              <p className="text-sm text-[#A1A1AA] mt-3">Preview isn't available for this file type.</p>
              <a
                href={url}
                download={doc.label}
                data-testid="record-preview-download"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-5 py-2.5 min-h-[44px]"
              >
                <Download size={16} /> Download
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
